/**
 * Fix Z (2026-09-02): Ingeniería → Asistencias. Órdenes de trabajo (OT) de
 * asistencia técnica que abre el Operario Electricista: descripción, fecha de
 * inicio, cantidad y nombres de las personas, obra; después carga día por día
 * quiénes trabajaron y cuántas horas; al cerrar, el sistema genera la
 * solicitud de presupuesto en Presupuestos → Varios con la mano de obra
 * calculada (jornales vigentes) y los materiales pedidos contra la OT.
 * El operario no ve montos: el backend no se los manda (MONTOS_ROLES).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Wrench, Plus, Search, Loader2, X, Calendar, Users, CheckCircle2, Lock, Trash2, Save, ClipboardList, ExternalLink } from "lucide-react";
import { API, useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { formatDate, formatPYG, todayAsuncion } from "../../utils/format";

const opts = { withCredentials: true };
const ROLES_PERSONA = [{ value: "oficial", label: "Oficial" }, { value: "ayudante", label: "Ayudante" }];

const blankForm = () => ({ descripcion: "", fecha_inicio: todayAsuncion(), obra: "", lista_obra_id: "", lista_cliente_id: "", personas: [{ nombre: "", rol: "ayudante" }] });
// Fix DP (2026-09-22): el día se carga con horario; las horas salen de la resta.
const HORA_INICIO_POR_DEFECTO = "07:00";
const HORA_FIN_POR_DEFECTO = "15:00";
export const horasDelHorario = (ini, fin) => {
  const m = (h) => {
    const p = String(h || "").split(":");
    const hh = Number(p[0]), mm = Number(p[1]);
    return (Number.isFinite(hh) && Number.isFinite(mm)) ? hh * 60 + mm : null;
  };
  const a = m(ini), b = m(fin);
  if (a === null || b === null) return 0;
  let dif = b - a;
  if (dif < 0) dif += 24 * 60;
  return Math.round((dif / 60) * 100) / 100;
};

function Modal({ title, icon: Icon, onClose, children, wide, testid }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-4xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">{Icon && <Icon size={16} className="text-[#8B5CF6]" />} {title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid={`${testid}-close`}><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#8B5CF6]/20 focus:border-[#8B5CF6]";
const labelCls = "block text-xs font-semibold text-gray-700 mb-1";

export default function Asistencias() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState("abierta");
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [personal, setPersonal] = useState([]);
  const [obras, setObras] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [otAbiertas, setOtAbiertas] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/asistencias`, { ...opts, params: estado !== "todas" ? { estado } : {} });
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudieron cargar las asistencias"); }
    finally { setLoading(false); }
  }, [estado]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/asistencias/personal`, opts).then(r => setPersonal(r.data || [])).catch(() => {});
    axios.get(`${API}/listas/obras`, opts).then(r => setObras(r.data || [])).catch(() => {});
    axios.get(`${API}/listas/clientes`, opts).then(r => setClientes(r.data || [])).catch(() => {});
    axios.get(`${API}/asistencias/abiertas`, opts).then(r => setOtAbiertas(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => items.filter(a => !search || [a.number, a.descripcion, a.obra, a.lista_obra_nombre, a.created_by_name].some(v => (v || "").toLowerCase().includes(search.toLowerCase()))), [items, search]);
  const verMontos = (a) => a?.resumen && a.resumen.costo_mo != null;

  const setPersona = (idx, campo, valor) => setForm(f => ({ ...f, personas: f.personas.map((p, i) => i === idx ? { ...p, [campo]: valor } : p) }));
  const addPersona = () => setForm(f => ({ ...f, personas: [...f.personas, { nombre: "", rol: "ayudante" }] }));
  const delPersona = (idx) => setForm(f => ({ ...f, personas: f.personas.filter((_, i) => i !== idx) }));
  const onNombre = (idx, valor) => {
    const w = personal.find(p => p.nombre === valor);
    setForm(f => ({ ...f, personas: f.personas.map((p, i) => i === idx ? { ...p, nombre: valor, rol: w ? w.rol : p.rol } : p) }));
  };

  const crear = async (e) => {
    e.preventDefault();
    const personas = form.personas.filter(p => p.nombre.trim());
    if (!form.descripcion.trim()) { toast.error("Describí el trabajo a ejecutar"); return; }
    if (!form.lista_obra_id && !form.obra.trim()) { toast.error("Indicá la obra o el lugar de la asistencia"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/asistencias`, { ...form, lista_obra_id: form.lista_obra_id || null, lista_cliente_id: form.lista_cliente_id || null, personas }, opts);
      toast.success(`Asistencia ${data.number} creada`);
      setShowNew(false); setForm(blankForm());
      setEstado("abierta"); load();
      axios.get(`${API}/asistencias/abiertas`, opts).then(r => setOtAbiertas(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo crear la asistencia"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="asistencias-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Wrench size={20} className="text-[#8B5CF6]" /> Asistencias técnicas</h2>
          <p className="text-xs text-gray-500 mt-0.5">Cada asistencia abre una orden de trabajo (OT). Cargá día por día quiénes trabajaron; al cerrarla se genera la solicitud de presupuesto en Varios.</p>
        </div>
        <button onClick={() => { setForm(blankForm()); setShowNew(true); }} className="flex items-center gap-2 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="asistencias-crear-btn">
          <Plus size={16} /> Crear asistencia
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por OT, descripción, obra o quien la creó..." className={`${inputCls} pl-9`} data-testid="asistencias-search" />
        </div>
        <select value={estado} onChange={e => setEstado(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white" data-testid="asistencias-estado-filter">
          <option value="abierta">Abiertas</option>
          <option value="cerrada">Cerradas</option>
          <option value="todas">Todas</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-sm text-gray-500" data-testid="asistencias-vacio">
          {estado === "abierta" ? "No hay asistencias abiertas. Creá una con el botón de arriba." : "Sin asistencias para este filtro."}
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(a => (
            <div key={a.id} className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`asistencia-card-${a.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-[#8B5CF6] bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">{a.number}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${a.estado === "abierta" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>{a.estado === "abierta" ? "Abierta" : "Cerrada"}</span>
                  {a.presupuesto_number && <span className="text-[10px] text-gray-500">→ {a.presupuesto_number}</span>}
                </div>
                <p className="text-sm font-semibold text-gray-900 mt-1 truncate" title={a.descripcion}>{a.descripcion}</p>
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                  <span>{a.lista_obra_nombre || a.obra}</span>
                  <span className="flex items-center gap-1"><Calendar size={11} /> {formatDate(a.fecha_inicio)}{a.fecha_fin ? ` → ${formatDate(a.fecha_fin)}` : " → abierta"}</span>
                  <span className="flex items-center gap-1"><Users size={11} /> {(a.dias || []).length} día(s) cargados · {a.resumen?.persona_dias || 0} persona-día · {a.resumen?.horas_total || 0} h</span>
                  {verMontos(a) && <span className="font-semibold text-gray-700">MO {formatPYG(a.resumen.costo_mo)}</span>}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">Creada por {a.created_by_name} · {formatDate(a.created_at)}</p>
              </div>
              <button onClick={() => setDetail(a)} className="flex items-center gap-1.5 border border-gray-300 hover:border-[#8B5CF6] text-gray-700 rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid={`asistencia-abrir-${a.id}`}>
                <ClipboardList size={13} /> {a.estado === "abierta" ? "Cargar días / cerrar" : "Ver detalle"}
              </button>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <Modal title="Crear asistencia" icon={Wrench} onClose={() => setShowNew(false)} testid="asistencia-nueva-modal">
          <form onSubmit={crear} className="space-y-4">
            <div>
              <label className={labelCls}>Descripción del trabajo a ejecutar <span className="text-red-500">*</span></label>
              <textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={3} className={inputCls} placeholder="Ej: Reparación de tablero seccional y cambio de llaves en planta baja" data-testid="asistencia-descripcion" />
            </div>
            <div>
              <label className={labelCls}>Fecha de inicio <span className="text-red-500">*</span></label>
              <input type="date" value={form.fecha_inicio} onChange={e => setForm({ ...form, fecha_inicio: e.target.value })} className={inputCls} data-testid="asistencia-fecha-inicio" />
              <p className="text-[10px] text-gray-400 mt-1">La fecha de culminación queda abierta: se define al cerrar la asistencia. Las personas se cargan día por día, más abajo.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Cliente (catálogo)</label>
                <select value={form.lista_cliente_id} onChange={e => setForm({ ...form, lista_cliente_id: e.target.value, lista_obra_id: "" })} className={inputCls} data-testid="asistencia-cliente">
                  <option value="">— Sin cliente —</option>
                  {clientes.map(c => <option key={c._id} value={c._id}>{`${c.codigo} · ${c.cliente}`}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Obra (catálogo)</label>
                <select value={form.lista_obra_id} onChange={e => setForm({ ...form, lista_obra_id: e.target.value })} className={inputCls} data-testid="asistencia-obra-catalogo">
                  <option value="">— Sin vincular —</option>
                  {(form.lista_cliente_id ? obras.filter(o => o.cliente_id === form.lista_cliente_id) : obras).map(o => <option key={o._id} value={o._id}>{`${o.codigo} · ${o.obra}`}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Obra / lugar de la asistencia {!form.lista_obra_id && <span className="text-red-500">*</span>}</label>
              <input value={form.obra} onChange={e => setForm({ ...form, obra: e.target.value })} className={inputCls} placeholder="Ej: Local MEDVITAL, San Lorenzo" data-testid="asistencia-obra" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls}>Personas que trabajarán (nombre y apellido)</label>
                <button type="button" onClick={addPersona} className="text-xs font-semibold text-[#8B5CF6] flex items-center gap-1" data-testid="asistencia-agregar-persona"><Plus size={12} /> Agregar</button>
              </div>
              <datalist id="asistencia-personal-list">{personal.map(p => <option key={p.id} value={p.nombre}>{p.cargo}</option>)}</datalist>
              <div className="space-y-2">
                {form.personas.map((p, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input list="asistencia-personal-list" value={p.nombre} onChange={e => onNombre(idx, e.target.value)} className={inputCls} placeholder="Nombre y apellido" data-testid={`asistencia-persona-nombre-${idx}`} />
                    <select value={p.rol} onChange={e => setPersona(idx, "rol", e.target.value)} className="border border-gray-300 rounded-xl px-2 py-2 text-sm bg-white w-32" data-testid={`asistencia-persona-rol-${idx}`}>
                      {ROLES_PERSONA.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <button type="button" onClick={() => delPersona(idx)} className="p-2 text-gray-400 hover:text-red-500" data-testid={`asistencia-persona-quitar-${idx}`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600">Cancelar</button>
              <button type="submit" disabled={saving} className="flex items-center gap-2 bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white rounded-xl px-4 py-2 text-sm font-semibold" data-testid="asistencia-guardar">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Crear asistencia
              </button>
            </div>
          </form>
        </Modal>
      )}

      {detail && (
        <DetalleAsistencia asistencia={detail} personal={personal} onClose={() => setDetail(null)}
          onChanged={(a) => { setDetail(a); load(); axios.get(`${API}/asistencias/abiertas`, opts).then(r => setOtAbiertas(r.data || [])).catch(() => {}); }}
          puedeVerPresupuestos={!["Operario Electricista", "Residente de Obra", "Ingeniero Residente"].includes(user?.role)} />
      )}
      {otAbiertas.length > 0 && <p className="text-[11px] text-gray-400">{otAbiertas.length} OT abierta(s) disponibles para imputar pedidos de materiales.</p>}
    </div>
  );
}

function DetalleAsistencia({ asistencia, personal, onClose, onChanged, puedeVerPresupuestos }) {
  const a = asistencia;
  const abierta = a.estado === "abierta";
  const [fecha, setFecha] = useState(todayAsuncion());
  const [filas, setFilas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [confirmCierre, setConfirmCierre] = useState(false);
  const [fechaFin, setFechaFin] = useState(todayAsuncion());
  const [obs, setObs] = useState("");
  const [fechaReporte, setFechaReporte] = useState(null);

  const cargarDia = (f) => {
    setFecha(f);
    const existente = (a.dias || []).find(d => d.fecha === f);
    if (existente) setFilas(existente.personas.map(p => ({
      ...p,
      hora_inicio: p.hora_inicio || HORA_INICIO_POR_DEFECTO,
      hora_fin: p.hora_fin || HORA_FIN_POR_DEFECTO,
    })));
    else setFilas((a.personas || []).map(p => ({ nombre: p.nombre, rol: p.rol, hora_inicio: HORA_INICIO_POR_DEFECTO, hora_fin: HORA_FIN_POR_DEFECTO })));
  };
  useEffect(() => {
    cargarDia(todayAsuncion());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.id]);

  const setFila = (i, campo, valor) => setFilas(fs => fs.map((f, idx) => idx === i ? { ...f, [campo]: valor } : f));
  const guardarDia = async (personas) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/asistencias/${a.id}/dias`, { fecha, personas }, opts);
      toast.success(personas.length ? `Día ${formatDate(fecha)} guardado: ${personas.length} persona(s)` : `Día ${formatDate(fecha)} borrado`);
      onChanged(data);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar el día"); }
    finally { setSaving(false); }
  };
  const cerrar = async () => {
    setCerrando(true);
    try {
      const { data } = await axios.post(`${API}/asistencias/${a.id}/cerrar`, { fecha_fin: fechaFin, observaciones: obs }, opts);
      toast.success(`Asistencia cerrada. Solicitud ${data.presupuesto_generado?.number || ""} generada en Presupuestos → Varios`);
      setConfirmCierre(false);
      onChanged(data);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo cerrar la asistencia"); }
    finally { setCerrando(false); }
  };
  const r = a.resumen || {};
  const verMontos = r.costo_mo != null;

  return (
    <Modal title={`${a.number} — ${abierta ? "Registro diario" : "Asistencia cerrada"}`} icon={ClipboardList} onClose={onClose} wide testid="asistencia-detalle-modal">
      <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 text-xs text-gray-700 space-y-1">
        <p className="font-semibold text-gray-900">{a.descripcion}</p>
        <p>{a.lista_obra_nombre || a.obra}{a.lista_cliente_nombre ? ` · ${a.lista_cliente_nombre}` : ""} · desde {formatDate(a.fecha_inicio)}{a.fecha_fin ? ` hasta ${formatDate(a.fecha_fin)}` : ""}</p>
        <p>{r.dias || 0} día(s) · {r.persona_dias || 0} persona-día · {r.horas_total || 0} h ({r.horas_oficial || 0} h oficial, {r.horas_ayudante || 0} h ayudante) · {r.jornales || 0} jornal(es){verMontos ? ` · Mano de obra ${formatPYG(r.costo_mo)}` : ""}</p>
        {a.presupuesto_number && (
          <p className="flex items-center gap-1">Solicitud de presupuesto: <span className="font-mono font-bold">{a.presupuesto_number}</span>
            {puedeVerPresupuestos && <a href="/ingenieria/presupuestos/varios" className="text-[#8B5CF6] font-semibold inline-flex items-center gap-1" data-testid="asistencia-ver-en-varios">ver en Varios <ExternalLink size={11} /></a>}
          </p>
        )}
      </div>

      {abierta && (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3" data-testid="asistencia-editor-dia">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-xs font-semibold text-gray-700">Día</label>
            <input type="date" value={fecha} min={a.fecha_inicio} onChange={e => cargarDia(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-1.5 text-sm" data-testid="asistencia-dia-fecha" />
            <button type="button" onClick={() => setFilas(fs => [...fs, { nombre: "", rol: "ayudante", hora_inicio: HORA_INICIO_POR_DEFECTO, hora_fin: HORA_FIN_POR_DEFECTO }])} className="text-xs font-semibold text-[#8B5CF6] flex items-center gap-1" data-testid="asistencia-dia-agregar"><Plus size={12} /> Agregar persona</button>
          </div>
          <div className="text-xs text-gray-500">
            <button type="button" onClick={() => setFechaReporte(fecha)} disabled={!(a.dias || []).some(d => d.fecha === fecha)} className="font-semibold text-violet-700 disabled:opacity-40" data-testid="asistencia-dia-fotos">Fotos e informe del día seleccionado</button>
            {!(a.dias || []).some(d => d.fecha === fecha) && <p>Guardá primero el día para adjuntar sus fotografías.</p>}
          </div>
          <datalist id="asistencia-personal-list-dia">{personal.map(p => <option key={p.id} value={p.nombre}>{p.cargo}</option>)}</datalist>
          {filas.map((f, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input list="asistencia-personal-list-dia" value={f.nombre} onChange={e => setFila(i, "nombre", e.target.value)} className={inputCls} placeholder="Nombre y apellido" data-testid={`asistencia-dia-nombre-${i}`} />
              <select value={f.rol} onChange={e => setFila(i, "rol", e.target.value)} className="border border-gray-300 rounded-xl px-2 py-2 text-sm bg-white w-32" data-testid={`asistencia-dia-rol-${i}`}>
                {ROLES_PERSONA.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
              </select>
              <input type="time" value={f.hora_inicio || ""} onChange={e => setFila(i, "hora_inicio", e.target.value)} className="border border-gray-300 rounded-xl px-2 py-2 text-sm w-28" title="Hora de inicio" data-testid={`asistencia-dia-inicio-${i}`} />
              <span className="text-xs text-gray-400">a</span>
              <input type="time" value={f.hora_fin || ""} onChange={e => setFila(i, "hora_fin", e.target.value)} className="border border-gray-300 rounded-xl px-2 py-2 text-sm w-28" title="Hora de fin" data-testid={`asistencia-dia-fin-${i}`} />
              <span className="text-xs font-semibold text-gray-500 w-14 text-right" data-testid={`asistencia-dia-horas-${i}`}>{horasDelHorario(f.hora_inicio, f.hora_fin)} h</span>
              <button type="button" onClick={() => setFilas(fs => fs.filter((_, idx) => idx !== i))} className="p-2 text-gray-400 hover:text-red-500" data-testid={`asistencia-dia-quitar-${i}`}><Trash2 size={14} /></button>
            </div>
          ))}
          <div className="flex justify-between items-center pt-1">
            <p className="text-[11px] text-gray-400">Las horas salen de la resta entre la hora de fin y la de inicio. Una jornada completa son 8 horas. Guardar reemplaza lo cargado para ese día.</p>
            <div className="flex gap-2">
              {(a.dias || []).some(d => d.fecha === fecha) && (
                <button type="button" onClick={() => guardarDia([])} disabled={saving} className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-xs font-semibold" data-testid="asistencia-dia-borrar">Borrar día</button>
              )}
              <button type="button" onClick={() => {
                const cargadas = filas.filter(f => f.nombre.trim());
                const sinHorario = cargadas.find(f => !f.hora_inicio || !f.hora_fin);
                if (sinHorario) { toast.error(`Cargá la hora de inicio y de fin de ${sinHorario.nombre}`); return; }
                const invalida = cargadas.find(f => !(horasDelHorario(f.hora_inicio, f.hora_fin) > 0));
                if (invalida) { toast.error(`El horario de ${invalida.nombre} no da horas trabajadas`); return; }
                guardarDia(cargadas.map(f => ({ nombre: f.nombre.trim(), rol: f.rol, hora_inicio: f.hora_inicio, hora_fin: f.hora_fin })));
              }} disabled={saving || !filas.some(f => f.nombre.trim())}
                className="flex items-center gap-1.5 bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="asistencia-dia-guardar">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar día
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Días registrados</p>
        {(a.dias || []).length === 0 ? (
          <p className="text-xs text-gray-400 italic">Todavía no hay días cargados.</p>
        ) : (
          <div className="space-y-2">
            {(a.dias || []).map(d => (
              <div key={d.fecha} className="border border-gray-100 rounded-xl px-3 py-2 flex items-start gap-3" data-testid={`asistencia-dia-${d.fecha}`}>
                <p className="text-xs font-bold text-gray-800 w-24 shrink-0">{formatDate(d.fecha)}</p>
                <ul className="text-xs text-gray-600 flex-1">
                  {d.personas.map((p, i) => <li key={i}>· {p.nombre} <span className="text-gray-400">({p.rol}{p.hora_inicio && p.hora_fin ? `, ${p.hora_inicio}–${p.hora_fin}` : ""}, {p.horas} h)</span></li>)}
                </ul>
                <button type="button" onClick={() => setFechaReporte(d.fecha)} className="block text-[11px] font-semibold text-violet-700" data-testid={`asistencia-dia-reporte-${d.fecha}`}>Fotos ({(d.fotos || []).length}) e informe{d.informe?.texto ? " · Borrador" : ""}</button>
                {abierta && <button type="button" onClick={() => cargarDia(d.fecha)} className="text-[11px] font-semibold text-[#8B5CF6]" data-testid={`asistencia-dia-editar-${d.fecha}`}>Editar</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {fechaReporte && (a.dias || []).some(d => d.fecha === fechaReporte) && <FotosInformeDia key={`${a.id}:${fechaReporte}`} asistencia={a} dia={a.dias.find(d => d.fecha === fechaReporte)} onChanged={onChanged} onClose={() => setFechaReporte(null)} />}

      {abierta && (
        <div className="border-t border-gray-100 pt-4">
          {!confirmCierre ? (
            <button type="button" onClick={() => setConfirmCierre(true)} disabled={!(a.dias || []).length} className="flex items-center gap-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 rounded-xl px-4 py-2 text-sm font-semibold" data-testid="asistencia-cerrar-btn">
              <Lock size={14} /> Cerrar asistencia y generar solicitud de presupuesto
            </button>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3" data-testid="asistencia-cierre-confirm">
              <p className="text-sm text-gray-800">Al cerrar, la fecha de culminación queda fijada y se genera automáticamente la solicitud en Presupuestos → Varios con la mano de obra de los días cargados y los materiales pedidos contra esta OT. No se puede reabrir.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Fecha de culminación</label>
                  <input type="date" value={fechaFin} min={a.fecha_inicio} onChange={e => setFechaFin(e.target.value)} className={inputCls} data-testid="asistencia-cierre-fecha" />
                </div>
                <div>
                  <label className={labelCls}>Observaciones (opcional)</label>
                  <input value={obs} onChange={e => setObs(e.target.value)} className={inputCls} placeholder="Ej: quedó pendiente la prueba final" data-testid="asistencia-cierre-obs" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setConfirmCierre(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600" data-testid="asistencia-cierre-volver">Volver</button>
                <button type="button" onClick={cerrar} disabled={cerrando} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-xl px-4 py-2 text-sm font-semibold" data-testid="asistencia-cierre-confirmar">
                  {cerrando ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Confirmar cierre
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function FotoRegistroDia({ asistenciaId, fecha, foto }) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState(false);
  useEffect(() => {
    let activa = true, blobUrl = "";
    axios.get(`${API}/asistencias/${asistenciaId}/dias/${fecha}/fotos/${foto.id}`, { ...opts, responseType: "blob" })
      .then(({ data }) => { blobUrl = URL.createObjectURL(data); if (activa) setUrl(blobUrl); else URL.revokeObjectURL(blobUrl); })
      .catch(() => { if (activa) setError(true); });
    return () => { activa = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [asistenciaId, fecha, foto.id]);
  return <div className="rounded-lg border p-2 text-xs">{url ? <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt={foto.filename || "Foto del trabajo de ese día"} className="h-28 w-full object-contain" /><span>{foto.filename || "Abrir foto"}</span></a> : <p>{error ? "No se pudo cargar la foto" : "Cargando foto…"}</p>}</div>;
}

function FotosInformeDia({ asistencia, dia, onChanged, onClose }) {
  const [descripcion, setDescripcion] = useState(dia.descripcion_trabajos || "");
  const [texto, setTexto] = useState(dia.informe?.texto || "");
  const [busy, setBusy] = useState("");
  const abierta = asistencia.estado === "abierta";
  const base = `${API}/asistencias/${asistencia.id}/dias/${dia.fecha}`;
  const guardar = async (incluirInforme = false) => {
    const { data } = await axios.put(`${base}/reporte`, { descripcion_trabajos: descripcion, ...(incluirInforme ? { informe_texto: texto } : {}) }, opts);
    onChanged(data);
    return data;
  };
  const guardarManual = async () => {
    setBusy("guardar");
    try { await guardar(true); toast.success("Borrador del día guardado para revisión"); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar el borrador"); }
    finally { setBusy(""); }
  };
  const subir = async e => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy("fotos");
    try {
      const body = new FormData(); files.forEach(f => body.append("fotos", f));
      const { data } = await axios.post(`${base}/fotos`, body, opts);
      onChanged(data); toast.success("Fotos adjuntadas a " + formatDate(dia.fecha));
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudieron adjuntar las fotos"); }
    finally { setBusy(""); }
  };
  const generar = async () => {
    setBusy("ia");
    try {
      await guardar(false);
      const { data } = await axios.post(`${base}/informe-ia`, {}, opts);
      const generado = (data.dias || []).find(d => d.fecha === dia.fecha);
      setTexto(generado?.informe?.texto || ""); onChanged(data);
      toast.success("Borrador generado: revisalo y corregilo antes de usarlo");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo generar el informe"); }
    finally { setBusy(""); }
  };
  return <section className="border border-violet-200 rounded-xl p-4 space-y-3" data-testid={`asistencia-reporte-${dia.fecha}`}>
    <div className="flex justify-between gap-2"><h4 className="font-bold text-sm">Fotos e informe · {formatDate(dia.fecha)}</h4><button type="button" onClick={onClose} className="text-xs">Cerrar panel</button></div>
    <p className="text-xs text-gray-500">Las fotografías y el informe pertenecen únicamente a esta fecha. El informe es un borrador revisable; no certifica trabajos ni envía mensajes.</p>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{(dia.fotos || []).map(f => <FotoRegistroDia key={f.id} asistenciaId={asistencia.id} fecha={dia.fecha} foto={f} />)}</div>
    {abierta && <label className="block text-xs font-semibold">Adjuntar fotografías<input type="file" accept="image/*" multiple disabled={!!busy} onChange={subir} className="block mt-1 text-xs" data-testid="asistencia-reporte-fotos" /></label>}
    <label className="block text-xs font-semibold">Descripción básica del trabajo<textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} readOnly={!abierta} disabled={!!busy} rows={3} className="block mt-1 w-full border rounded-lg p-2 font-normal" data-testid="asistencia-reporte-descripcion" /></label>
    {abierta && <>
      {(!(dia.fotos || []).length || !descripcion.trim()) && <p className="text-xs text-amber-700">Para generar con IA, adjuntá al menos una foto de este día y escribí su descripción.</p>}
      <button type="button" onClick={generar} disabled={!!busy || !(dia.fotos || []).length || !descripcion.trim()} className="border rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-40" data-testid="asistencia-reporte-generar">{busy === "ia" ? "Generando borrador…" : "Generar borrador con IA (opcional)"}</button>
    </>}
    {dia.informe_desactualizado && <p className="text-xs text-amber-700">Las fotos o la descripción cambiaron desde este borrador; revisalo antes de usarlo.</p>}
    <label className="block text-xs font-semibold">Informe del día · Borrador<textarea value={texto} onChange={e => setTexto(e.target.value)} readOnly={!abierta} disabled={!!busy} rows={7} className="block mt-1 w-full border rounded-lg p-2 font-normal" data-testid="asistencia-reporte-texto" /></label>
    {abierta && <button type="button" onClick={guardarManual} disabled={!!busy} className="bg-violet-600 text-white rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-40" data-testid="asistencia-reporte-guardar">{busy === "guardar" ? "Guardando…" : "Guardar descripción y borrador"}</button>}
  </section>;
}
